import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import cn from "classnames";
import { Tooltip } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";

import { getRoute } from "@/shared/utils/route/route.utils";
import { Loading } from "@/shared/components/Loading/Loading";

import { DEFAULT_LOCALE } from "@/i18n";
import { getTestsProps } from "@/modules/candidate/utils/candidateAssessment.utils";

import { useGetAssessment } from "@/modules/candidate/hooks/useGetAssessment.hook";
import { ITextLocation } from "@/modules/assessments/scenes/CreateAssessment/assessment.interface";
import { AssessmentInviteStatus } from "@/modules/assessments/scenes/AssessmentInvites/invites.constants";

import { Button, ButtonSize } from "@/ui-components/commonComponents/Button/Button";
import MarkdownPreviewCustom from "@/ui-components/commonComponents/MarkdownPreviewCustom/MarkdownPreviewCustom";
import ParagraphWithIcon from "@/ui-components/commonComponents/ParagraphWithIcon/ParagraphWithIcon";

import { ReactComponent as DotSVG } from "@/assets/icons/feather-icons/circle.svg";

import style from "./style.module.css";

interface IInviteCard {
  assessmentTitle: string;
  assessmentId: number;
  inviteCode: string;
  state: AssessmentInviteStatus;
  textLocations: ITextLocation[];
  timerDisabled: boolean;
}

export const statusText: { [text: string]: string } = {
  [AssessmentInviteStatus.Accepted]: "Accepted",
  [AssessmentInviteStatus.Invited]: "Invited",
  [AssessmentInviteStatus.InProgress]: "In Progress",
  [AssessmentInviteStatus.Done]: "Finished",
  [AssessmentInviteStatus.Started]: "Started",
  [AssessmentInviteStatus.Rescinded]: "Rescinded",
};

const buttonTitle: { [text: string]: string } = {
  [AssessmentInviteStatus.Accepted]: "Start",
  [AssessmentInviteStatus.Invited]: "Start",
  [AssessmentInviteStatus.Started]: "Continue",
  [AssessmentInviteStatus.InProgress]: "Continue",
};

export function InviteCard({
  assessmentTitle,
  assessmentId,
  inviteCode,
  state,
  textLocations,
  timerDisabled,
}: IInviteCard) {
  const navigate = useNavigate();

  const { pathname } = useLocation();

  const { assessment, isLoading } = useGetAssessment({ assessmentId, locale: DEFAULT_LOCALE });

  const { leftTime, introText, outroText } = useMemo(() => {
    return getTestsProps({ assessment, textLocations });
  }, [assessment, textLocations]);

  const handleInviteCardButtonClick = (): void => {
    navigate(getRoute(pathname, inviteCode));
  };

  if (isLoading) {
    return (
      <div className={style.cardContainer}>
        <Loading />
      </div>
    );
  }

  return (
    <div
      className={cn(style.cardContainer, state === AssessmentInviteStatus.Rescinded && style.cardContainerRescinded)}
    >
      <div className={style.cardContent}>
        <div className={style.headerContainer}>
          <div className={cn(style.badge, style[state])}>
            <DotSVG aria-hidden="true" />
            {statusText[state]}
          </div>
          <Tooltip title={assessmentTitle} color="#747474" placement="topLeft">
            <p className={style.assessmentTitle}>{assessmentTitle}</p>
          </Tooltip>
        </div>

        <div className={style.statisticsContainer}>
          <ParagraphWithIcon text={timerDisabled ? "Untimed" : `${leftTime ? leftTime : 0}`}>
            <ClockCircleOutlined aria-label="Estimated time" />
          </ParagraphWithIcon>
        </div>

        <div className={style.descriptionContainer}>
          <MarkdownPreviewCustom
            source={state === AssessmentInviteStatus.Done ? outroText : introText}
            style={{ fontWeight: 400 }}
          />
        </div>
      </div>

      {state !== AssessmentInviteStatus.Done && state !== AssessmentInviteStatus.Rescinded && (
        <div className={style.buttonContainer}>
          <Button buttonSize={ButtonSize.small} fullWidth={true} onClick={handleInviteCardButtonClick}>
            {buttonTitle[state]}
          </Button>
        </div>
      )}
    </div>
  );
}
